import React, { useState } from 'react';
import {
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import {
  PhoneIcon as PhoneIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ContactSupport = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    priority: 'medium',
    category: '',
    subject: '',
    message: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Support team members
  const supportTeam = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'IT Support Lead',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b510?w=80&h=80&fit=crop&crop=face',
      specialties: ['Technical Issues', 'System Access', 'Password Reset'],
      email: 'sarah.chen@apollo.health',
      phone: '+1-555-0123',
      availability: 'Mon-Fri 8AM-6PM',
      languages: ['English', 'Mandarin']
    },
    {
      id: 2,
      name: 'Marcus Johnson',
      role: 'HR Support Specialist',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
      specialties: ['HR Policies', 'Leave Management', 'Benefits'],
      email: 'marcus.johnson@apollo.health',
      phone: '+1-555-0456',
      availability: 'Mon-Fri 9AM-5PM',
      languages: ['English', 'Spanish']
    },
    {
      id: 3,
      name: 'Dr. Priya Sharma',
      role: 'Training Coordinator',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=80&h=80&fit=crop&crop=face',
      specialties: ['Training Programs', 'Certifications', 'Learning Path'],
      email: 'priya.sharma@apollo.health',
      phone: '+1-555-0789',
      availability: 'Mon-Fri 8AM-4PM',
      languages: ['English', 'Hindi']
    },
    {
      id: 4,
      name: 'Alex Rodriguez',
      role: 'General Support',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
      specialties: ['General Inquiries', 'Escalation', 'Emergency Support'],
      email: 'alex.rodriguez@apollo.health',
      phone: '+1-555-0321',
      availability: '24/7 On-Call',
      languages: ['English', 'Spanish', 'Portuguese']
    }
  ];

  // Contact methods
  const contactMethods = [
    {
      icon: PhoneIconSolid,
      title: 'Phone Support',
      description: 'Speak directly with our support team for urgent issues',
      primary: '+1-800-APOLLO (275-5566)',
      secondary: 'Emergency: +1-800-911-HELP',
      availability: '24/7 Emergency | Mon-Fri 8AM-8PM General',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    },
    {
      icon: EnvelopeIconSolid,
      title: 'Email Support',
      description: 'Send detailed inquiries and receive comprehensive responses',
      primary: 'support@apollo.health',
      secondary: 'urgent@apollo.health',
      availability: 'Response within 4 hours | 24 hours for general inquiries',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-200'
    },
    {
      icon: ChatBubbleLeftRightIconSolid,
      title: 'Live Chat',
      description: 'Get instant help through our real-time chat system',
      primary: 'Available on all pages',
      secondary: 'AI Assistant 24/7',
      availability: 'Live agents: Mon-Fri 8AM-8PM | AI: 24/7',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200'
    }
  ];

  // Office locations
  const officeLocations = [
    {
      name: 'Main Campus',
      address: '123 Healthcare Blvd, Medical District, NY 10001',
      phone: '+1-555-0100',
      hours: 'Mon-Fri 7AM-7PM, Sat 8AM-4PM',
      services: ['IT Support Desk', 'HR Office', 'Training Center'],
      mapUrl: '#'
    },
    {
      name: 'Research Facility',
      address: '456 Innovation Drive, Research Park, NY 10002',
      phone: '+1-555-0200',
      hours: 'Mon-Fri 8AM-6PM',
      services: ['Technical Support', 'Equipment Requests'],
      mapUrl: '#'
    },
    {
      name: 'Satellite Office',
      address: '789 Community Center, Downtown, NY 10003',
      phone: '+1-555-0300',
      hours: 'Mon-Fri 9AM-5PM',
      services: ['General Support', 'Document Services'],
      mapUrl: '#'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    
    // Reset form after success
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        name: '',
        email: '',
        department: '',
        priority: 'medium',
        category: '',
        subject: '',
        message: '',
        phone: ''
      });
    }, 3000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Custom Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center justify-center">
                <UserGroupIcon className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary mr-2 sm:mr-3" />
                Contact Support
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Get personalized help from our expert support team
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Contact Methods */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
            Choose Your Preferred Contact Method
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className={`${method.bgColor} rounded-xl p-4 sm:p-6 border-2 ${method.borderColor} hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
              >
                <div className="text-center">
                  <method.icon className={`w-10 h-10 sm:w-12 sm:h-12 ${method.color} mx-auto mb-3 sm:mb-4`} />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{method.description}</p>
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-900">{method.primary}</p>
                    <p className="text-sm text-gray-600">{method.secondary}</p>
                    <div className="flex items-center justify-center text-xs text-gray-500 mt-3">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      <span>{method.availability}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-primary mr-2" />
                Send Us a Message
              </h2>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                Fill out the form below and we'll get back to you within 4 hours
              </p>
            </div>
            <div className="p-4 sm:p-6">
              {showSuccess ? (
                <div className="text-center py-8">
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Message Sent Successfully!</h3>
                  <p className="text-gray-600">
                    We've received your message and will respond within 4 hours.
                    You'll receive a confirmation email shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                        placeholder="your.email@apollo.health"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <select
                        name="department"
                        value={formData.department}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                      >
                        <option value="">Select Department</option>
                        <option value="cardiology">Cardiology</option>
                        <option value="emergency">Emergency</option>
                        <option value="neurology">Neurology</option>
                        <option value="pediatrics">Pediatrics</option>
                        <option value="radiology">Radiology</option>
                        <option value="nursing">Nursing</option>
                        <option value="administration">Administration</option>
                        <option value="it">IT Department</option>
                        <option value="hr">Human Resources</option>
                        <option value="finance">Finance</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                        placeholder="+1-555-0123"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Level
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                      >
                        <option value="low">Low - General Inquiry</option>
                        <option value="medium">Medium - Standard Support</option>
                        <option value="high">High - Urgent Issue</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                      >
                        <option value="">Select Category</option>
                        <option value="technical">Technical Support</option>
                        <option value="hr">HR & Policies</option>
                        <option value="training">Training & Learning</option>
                        <option value="facilities">Facilities</option>
                        <option value="payroll">Payroll & Benefits</option>
                        <option value="access">Access & Permissions</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary"
                      placeholder="Brief description of your issue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary resize-none"
                      placeholder="Please provide detailed information about your request or issue..."
                    />
                  </div>

                  {/* Priority Preview */}
                  {formData.priority && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Priority:</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(formData.priority)}`}>
                          <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                          {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {formData.priority === 'high' && 'Response within 1 hour during business hours'}
                        {formData.priority === 'medium' && 'Response within 4 hours during business hours'}
                        {formData.priority === 'low' && 'Response within 24 hours'}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center px-6 py-3 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Support Team & Office Information */}
          <div className="space-y-6">
            {/* Support Team */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <UserGroupIcon className="w-6 h-6 text-apollo-primary mr-2" />
                  Our Support Team
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {supportTeam.map((member) => (
                    <div key={member.id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start space-x-4">
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="w-12 h-12 rounded-full"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{member.name}</h3>
                          <p className="text-sm text-apollo-primary">{member.role}</p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {member.specialties.map((specialty, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-apollo-accent/10 text-apollo-accent rounded text-xs"
                              >
                                {specialty}
                              </span>
                            ))}
                          </div>
                          <div className="mt-2 text-xs text-gray-600 space-y-1">
                            <div className="flex items-center">
                              <EnvelopeIcon className="w-3 h-3 mr-1" />
                              <a href={`mailto:${member.email}`} className="hover:text-apollo-primary">
                                {member.email}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <PhoneIcon className="w-3 h-3 mr-1" />
                              <a href={`tel:${member.phone}`} className="hover:text-apollo-primary">
                                {member.phone}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <ClockIcon className="w-3 h-3 mr-1" />
                              <span>{member.availability}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Office Locations */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <MapPinIcon className="w-6 h-6 text-apollo-primary mr-2" />
                  Office Locations
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {officeLocations.map((location, index) => (
                    <div key={index} className="border border-gray-100 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{location.name}</h3>
                      <div className="text-sm text-gray-600 space-y-2">
                        <div className="flex items-start">
                          <MapPinIcon className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                          <span>{location.address}</span>
                        </div>
                        <div className="flex items-center">
                          <PhoneIcon className="w-4 h-4 mr-2" />
                          <a href={`tel:${location.phone}`} className="hover:text-apollo-primary">
                            {location.phone}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 mr-2" />
                          <span>{location.hours}</span>
                        </div>
                        <div className="mt-3">
                          <p className="font-medium text-gray-700 mb-1">Services Available:</p>
                          <div className="flex flex-wrap gap-1">
                            {location.services.map((service, serviceIndex) => (
                              <span
                                key={serviceIndex}
                                className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent rounded-xl text-white p-6">
              <h2 className="text-xl font-bold mb-4">Need Immediate Assistance?</h2>
              <div className="space-y-3">
                <a
                  href="tel:+1-800-275-5566"
                  className="flex items-center justify-between bg-white/10 rounded-lg p-3 hover:bg-white/20 transition-colors"
                >
                  <div className="flex items-center">
                    <PhoneIcon className="w-5 h-5 mr-3" />
                    <span className="font-medium">Call Support Hotline</span>
                  </div>
                  <span className="text-sm opacity-80">24/7 Available</span>
                </a>
                <button className="flex items-center justify-between bg-white/10 rounded-lg p-3 hover:bg-white/20 transition-colors w-full">
                  <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 mr-3" />
                    <span className="font-medium">Start Live Chat</span>
                  </div>
                  <span className="text-sm opacity-80">Mon-Fri 8AM-8PM</span>
                </button>
                <button className="flex items-center justify-between bg-white/10 rounded-lg p-3 hover:bg-white/20 transition-colors w-full">
                  <div className="flex items-center">
                    <VideoCameraIcon className="w-5 h-5 mr-3" />
                    <span className="font-medium">Schedule Video Call</span>
                  </div>
                  <span className="text-sm opacity-80">By Appointment</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ContactSupport;
